/* The Metadata Copilot on the front page: a door into it, in the Overview's own surface language.
 *
 * **It claims nothing the copilot did not do.** The copilot answers questions; it does not watch the
 * estate or decide what needs attention — the platform's monitors and queues do, and those items are
 * already in "Needs attention", labelled as what they are. Writing "the copilot found 3 issues" here
 * would attribute the platform's own detections to a model, which is the inversion
 * `ValidatedCompletion` and the copilot screen's provenance labels exist to prevent.
 *
 * **Two starter questions, both generic.** They are the copilot screen's own examples, read from the
 * same catalogue keys so the two surfaces cannot drift apart. The third example there names a source
 * by name, and on a tenant without that source it would be a question about something that does not
 * exist — so it is left on the screen where it reads as an example rather than a suggestion.
 *
 * **A question is handed over, never asked.** Each starter opens `/copilot?q=…`, which fills the
 * composer and stops there, matching that screen's rule that nobody spends a completion by brushing a
 * suggestion.
 *
 * The mark breathes at idle, turns while the page's queues are still being read, and pulses once when
 * something is waiting for a person — motion only, never the only signal.
 */

import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { SCREEN_PATHS } from '@/shared/routes';

import { CopilotMark } from './CopilotMark';
import { ArrowIcon } from './icons';
import { Panel } from './OverviewParts';

/** The copilot screen's generic starters, by the same catalogue keys it reads. */
const STARTERS = ['copilot.starter.profiled', 'copilot.starter.contracts'] as const;

export function CopilotPanel({
  busy,
  waiting,
}: {
  readonly busy: boolean;
  readonly waiting: boolean;
}): ReactElement {
  const { t } = useTranslation();
  return (
    <Panel title={t('home.copilot.title')} className="lq-ov-copilot">
      <div className="lq-ov-copilot__intro">
        <CopilotMark busy={busy} signal={waiting} />
        <p className="lq-ov-copilot__lede">{t('home.copilot.lede')}</p>
      </div>
      <ul className="lq-ov-copilot__asks">
        {STARTERS.map((key) => (
          <li key={key}>
            <Link
              className="lq-ov-copilot__ask"
              to={`${SCREEN_PATHS.copilot}?q=${encodeURIComponent(t(key))}`}
            >
              <span>{t(key)}</span>
              <ArrowIcon className="lq-ov-icon lq-ov-icon--arrow" />
            </Link>
          </li>
        ))}
      </ul>
      <Link className="lq-ov-button" to={SCREEN_PATHS.copilot}>
        {t('home.copilot.open')}
      </Link>
    </Panel>
  );
}
